import { Linkedin, Instagram, Facebook, Youtube, MapPin, Phone, Mail, MessageCircle, ArrowRight } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { CONTACT, WHATSAPP_LINK, SOCIAL_LINKS } from "@/lib/site";

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", testId: "social-linkedin", href: SOCIAL_LINKS.linkedin },
  { icon: Instagram, label: "Instagram", testId: "social-instagram", href: SOCIAL_LINKS.instagram },
  { icon: Facebook, label: "Facebook", testId: "social-facebook", href: SOCIAL_LINKS.facebook },
  { icon: Youtube, label: "YouTube", testId: "social-youtube", href: SOCIAL_LINKS.youtube },
];

const FOLLOW_POINTS = [
  "Healthcare knowledge",
  "Hospital management insights",
  "Insurance & cashless awareness",
  "Healthcare business updates",
  "Healthcare Connect interviews",
  "Public healthcare awareness",
];

const FOOTER_COLS = [
  {
    title: "Company",
    links: [
      { label: "About Us", href: "#about" },
      { label: "Our Services", href: "#services" },
      { label: "Healthcare Connect", href: "#connect" },
      { label: "Healthcare Knowledge", href: "#knowledge" },
      { label: "Contact", href: "#enquiry" },
    ],
  },
  {
    title: "For Hospitals",
    links: [
      { label: "Hospital Consultancy", href: "#services" },
      { label: "Insurance & TPA Support", href: "#services" },
      { label: "Cashless Process Support", href: "#services" },
      { label: "Hospital Empanelment", href: "#services" },
      { label: "Staff Training", href: "#services" },
    ],
  },
  {
    title: "For Patients",
    links: [
      { label: "Healthcare Awareness", href: "#patients" },
      { label: "Insurance Basics", href: "#knowledge" },
      { label: "Cashless Awareness", href: "#knowledge" },
      { label: "Healthcare Knowledge", href: "/knowledge" },
      { label: "Pre-Auth Forms & Resources", href: "/resources" },
    ],
  },
  {
    title: "Locations",
    links: [
      { label: "Madhya Pradesh", href: "#regional" },
      { label: "Chhattisgarh", href: "#regional" },
      { label: "Maharashtra", href: "#regional" },
    ],
  },
];

export const SocialFinal = () => (
  <>
    <section id="social" className="relative bg-white py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionTag index="13" label="Social Media" />
          <div className="mt-6 grid grid-cols-1 items-end gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-[#0B3C5C] sm:text-3xl lg:text-4xl">
                Follow Healthcare Solutions
              </h2>
              <p className="font-display mt-3 text-lg text-[#5FA832]">
                Healthcare Insights. Hospital Growth. Healthcare Awareness.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target={s.href.startsWith("http") ? "_blank" : undefined}
                  rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={s.testId}
                  aria-label={s.label}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-[#0B3C5C]/15 text-[#33586F] transition-all duration-300 hover:-translate-y-1 hover:border-[#5FA832] hover:text-[#4C8F2F]"
                >
                  <s.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2">
            {FOLLOW_POINTS.map((f) => (
              <span key={f} className="flex items-center gap-2 text-sm text-[#4A6B82]">
                <span className="h-1 w-1 rotate-45 bg-[#5FA832]" />
                {f}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <section id="final-cta" className="relative overflow-hidden bg-gradient-to-b from-white via-[#EAF3F8] to-[#F4FAF7] py-28 sm:py-36">
      <div className="hero-grid-lines absolute inset-0" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#5FA832]/10 blur-[130px]" />
      <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
        <Reveal>
          <SectionTag index="14" label="Final Call" />
          <h2 className="font-display mx-auto mt-8 text-3xl font-extrabold leading-tight tracking-tight text-[#0B3C5C] sm:text-4xl lg:text-5xl">
            Let's Build a Better Healthcare Ecosystem{" "}
            <span className="text-gradient-teal">Together.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-[#33586F] sm:text-lg">
            From hospitals and healthcare professionals to patients and healthcare businesses,
            every connection matters. Healthcare Solutions is working towards creating meaningful
            connections, practical healthcare solutions and accessible healthcare knowledge.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href="#enquiry"
              data-testid="final-cta-consultation"
              className="group inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-9 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F] hover:shadow-xl hover:shadow-[#5FA832]/25"
            >
              Request a Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#enquiry"
              data-testid="final-cta-contact"
              className="rounded-full border border-[#0B3C5C]/40 px-9 py-4 text-sm font-semibold text-[#33586F] transition-all duration-300 hover:border-[#5FA832] hover:text-[#5FA832]"
            >
              Contact Us
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 px-9 py-4 text-sm font-semibold text-emerald-600 transition-all duration-300 hover:bg-emerald-500/10"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Us
            </a>
          </div>
        </Reveal>
      </div>
    </section>

    <footer className="border-t border-white/10 bg-[#0B3C5C] py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <span className="inline-flex items-center rounded-2xl bg-white px-5 py-3 shadow-lg shadow-black/20">
              <img src="/logo.png" alt="Healthcare Solutions" className="h-12 w-auto" />
            </span>
            <p className="font-display mt-4 text-sm text-teal-300">Connecting Healthcare. Creating Solutions.</p>
            <p className="mt-4 max-w-xs text-xs leading-relaxed text-slate-400">
              Hospital Consultancy | Insurance & TPA Support | Cashless Process Support |
              Healthcare Training | Healthcare Awareness | Healthcare Networking
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-slate-300">
              <p className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 shrink-0 text-teal-500" /> {CONTACT.address}
              </p>
              <p className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 shrink-0 text-teal-500" />
                <a href={`tel:${CONTACT.phone}`} data-testid="footer-phone" className="transition-colors hover:text-teal-300">{CONTACT.phone}</a>
              </p>
              <p className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 shrink-0 text-teal-500" />
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="transition-colors hover:text-teal-300">{CONTACT.email}</a>
              </p>
              <p className="flex items-center gap-2.5">
                <MessageCircle className="h-4 w-4 shrink-0 text-teal-500" />
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="transition-colors hover:text-teal-300">
                  WhatsApp: {CONTACT.phone}
                </a>
              </p>
            </div>
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <p className="font-mono-x text-[10px] font-bold uppercase tracking-[0.3em] text-teal-300">
                {col.title}
              </p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="text-sm text-slate-300 transition-colors hover:text-teal-300">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Healthcare Solutions. All Rights Reserved.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {[
              { label: "Privacy Policy", href: "/legal/privacy-policy" },
              { label: "Terms & Conditions", href: "/legal/terms-and-conditions" },
              { label: "Disclaimer", href: "/legal/disclaimer" },
              { label: "Cookie Policy", href: "/legal/cookie-policy" },
            ].map((l) => (
              <a key={l.label} href={l.href} data-testid={`footer-${l.href.split("/").pop()}`} className="text-xs text-slate-400 transition-colors hover:text-teal-300">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  </>
);
